import { LIMITS } from '@wiztime/core';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useRunCommand } from '../../../hooks/useRunCommand';
import { useStore } from '../../../hooks/useServices';
import { Button, ButtonVariant } from '../../../ui/Button';
import popover from '../../../ui/AddPopover.module.css';
import { PlannedTimeField } from '../../checkin/PlannedTimeField';
import { checkCheckinName, NameIssue } from '../../settings/settingsValidation';

interface CheckinAddFormProps {
  /** Names of every check-in item (deactivated ones too) for the duplicate check. */
  readonly names: readonly string[];
  /** The item was created: close the popover. */
  readonly onDone: () => void;
}

/**
 * Content of the check-in card's add popover (2026-10c §4): a name (required, same validation
 * as Settings → Check-in) and an optional planned time. Enter or the button adds; an empty,
 * too long or duplicate name and an unparsable time are shown here and keep the popover open,
 * as does a failed write (its error toast is raised by the command runner).
 */
export function CheckinAddForm({ names, onDone }: CheckinAddFormProps) {
  const { t } = useTranslation();
  const store = useStore();
  const run = useRunCommand();
  const formRef = useRef<HTMLFormElement>(null);
  const [name, setName] = useState('');
  const [plannedMin, setPlannedMin] = useState<number | null>(null);
  // Written in the commit callback so a submit that follows at once reads the new value.
  const plannedRef = useRef<number | null>(null);
  const [plannedInvalid, setPlannedInvalid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  // Synchronous guard: state is stale between two quick Enters, a ref is not.
  const busyRef = useRef(false);
  const enterTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (enterTimer.current !== null) window.clearTimeout(enterTimer.current);
    },
    [],
  );

  const check = checkCheckinName(name);
  const error = !check.ok
    ? check.issue === NameIssue.Empty
      ? t('settings.validation.nameEmpty')
      : t('settings.validation.nameTooLong', { max: LIMITS.checkinNameMax })
    : names.includes(check.value)
      ? t('checkin.nameDuplicate')
      : null;

  const submit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (busyRef.current) return;
    setSubmitted(true);
    if (error !== null || !check.ok || plannedInvalid) return;
    const planned = plannedRef.current;
    busyRef.current = true;
    setBusy(true);
    try {
      const result = await run(() =>
        store.upsertCheckinItem(
          planned === null ? { name: check.value } : { name: check.value, plannedMin: planned },
        ),
      );
      if (result.ok) onDone();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <form ref={formRef} className={popover.form} noValidate onSubmit={(event) => void submit(event)}>
      <label className={popover.field}>
        <span className={popover.label}>{t('settings.checkin.name')}</span>
        <input
          className={popover.input}
          value={name}
          maxLength={LIMITS.checkinNameMax}
          aria-invalid={submitted && error !== null}
          autoComplete="off"
          placeholder={t('settings.checkin.placeholder')}
          data-testid="checkin-add-name"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            void submit();
          }}
        />
      </label>
      {submitted && error !== null ? (
        <p className={popover.error} role="alert" data-testid="checkin-add-error">
          {error}
        </p>
      ) : null}
      <div className={popover.field}>
        <span className={popover.label}>{t('settings.checkin.plannedTime')}</span>
        <PlannedTimeField
          value={plannedMin}
          itemName={name.trim()}
          testId="checkin-add-planned"
          onCommit={(next) => {
            plannedRef.current = next;
            setPlannedMin(next);
          }}
          onInvalidChange={setPlannedInvalid}
          // The field has committed by now; submit after the state it set has rendered.
          onEnter={() => {
            if (enterTimer.current !== null) window.clearTimeout(enterTimer.current);
            enterTimer.current = window.setTimeout(() => {
              enterTimer.current = null;
              formRef.current?.requestSubmit();
            }, 0);
          }}
        />
      </div>
      <div className={popover.actions}>
        <Button
          type="submit"
          variant={ButtonVariant.Primary}
          disabled={busy}
          data-testid="checkin-add-submit"
        >
          {t('settings.checkin.add')}
        </Button>
      </div>
    </form>
  );
}
